import { AxiosError } from 'axios'

/**
 * Mensagens que o `Handler` da API devolve em inglês e que nunca devem chegar à tela.
 * Ver `app/Exceptions/Handler.php`: uma validação responde 400 com
 * `{ message: 'Validation error', errors: { campo: [...] } }` — a frase em pt-BR está
 * no `errors`, não no `message`.
 */
const PLACEHOLDER_MESSAGES = ['Validation error', 'Resource not found', 'Server Error', 'Unauthenticated.']

const NETWORK_MESSAGE = 'Não foi possível falar com o servidor. Verifique sua conexão e tente novamente.'

interface IApiErrorBody {
  message?: unknown
  error?: unknown
  errors?: unknown
}

function firstFieldMessage(errors: unknown): string | undefined {
  if (!errors || typeof errors !== 'object') {
    return undefined
  }

  for (const value of Object.values(errors as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim()) {
      return value
    }

    if (Array.isArray(value)) {
      const message = value.find((item): item is string => typeof item === 'string' && item.trim().length > 0)
      if (message) {
        return message
      }
    }
  }

  return undefined
}

function usableMessage(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.trim() || PLACEHOLDER_MESSAGES.includes(value)) {
    return undefined
  }

  return value
}

/**
 * Mensagem de erro da API pronta para virar a descrição de um toast.
 *
 * A ordem importa porque a API não tem uma forma só: os controllers respondem tanto em
 * `message` quanto em `error`, e a validação põe a frase legível dentro de `errors`.
 * Ler só `response.data.message` — o que 27 páginas faziam à mão — engolia todos os
 * erros que vêm em `error` e mostrava "Validation error" em inglês nos de validação.
 *
 * Devolve `undefined` quando não há nada aproveitável, para quem chama ficar só com o
 * próprio título em vez de exibir uma frase genérica inventada aqui.
 */
export function getApiErrorMessage(error: unknown): string | undefined {
  const axiosError = error as AxiosError<IApiErrorBody> | undefined

  if (!axiosError?.isAxiosError) {
    return error instanceof Error ? usableMessage(error.message) : undefined
  }

  // Sem `response` o request não chegou ao servidor (offline, DNS, CORS, timeout).
  if (!axiosError.response) {
    return NETWORK_MESSAGE
  }

  const body = axiosError.response.data

  return firstFieldMessage(body?.errors) ?? usableMessage(body?.message) ?? usableMessage(body?.error)
}
